import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api, errorText } from '../api';
import { NutzungHinweis } from '../billtoro/NutzungHinweis';
import { fehlendeFunktion, grenzeAnbieten, upgradeAnbieten } from '../billtoro/upgrade';
import { ConfirmDialog, Dialog } from '../components/Dialog';
import { t } from '../i18n';
import { fmtDate, MONTHS } from '../lib/format';
import { betragText, daysUntil, eurC, inEurZahl, parseEuro } from '../lib/money';
import { useToast } from '../lib/toast';
import { isBilltoro } from '../produkt';
import { neuerLieferschein } from './AuftragDaten';
import { ChipRadios } from './ChipRadios';
import { DatevDialog } from './DatevDialog';
import { Entwuerfe } from './Entwuerfe';
import { ExportDialog } from './ExportDialog';
import { skontoBetrag } from './formular';
import { StundenzettelFenster } from './StundenzettelFenster';
import { PdfDialog } from './PdfDialog';
import { PostDialog, postText, type PostBeleg } from './PostDialog';
import { KorrekturDialog, type KorrekturErgebnis } from './KorrekturDialog';
import { mahnstoppUrl } from './MahnlaufDaten';
import { MahnlaufHinweis } from './MahnlaufHinweis';
import {
  gemindert,
  infoZeile,
  isOpen,
  isOverdue,
  mahnKnopf,
  menueEintraege,
  naechsterSchritt,
  type Eintrag,
  type Schritt,
  type ZeilenKontext,
} from './RechnungZeile';
import { darf, GesperrtKnopf, grenzeFehlt, NurAnsehen, tarifFehler } from './tarifHinweis';
import { MAHN_TITLES, type KundenArt, type Overview, type RtItem, type ZahlArt, type ZahlEintrag } from './types';
import { VersandDialog, type VersandBeleg } from './VersandDialog';
import { VerschickenDialog, type VerschickenBeleg } from './VerschickenDialog';
import { ausEur } from './waehrung';
import { ZeilenMenue } from './ZeilenMenue';
import './abos.css';
import './mahnlauf.css';

const BILLTORO = isBilltoro();

const pdfUrl = (id: string) => `/api/rechnungstool/rechnung/${id}/pdf`;
const mahnUrl = (id: string) => `/api/rechnungstool/mahnung/${id}/pdf`;
// beleg: „Per E-Mail“ im Fenster der PDF
type View = { url: string; title: string; beleg?: VersandBeleg };
// Geld (ohne Ausgebuchtes)
const geld = (i: RtItem) => Math.round((i.bezahlt - i.ausgebucht) * 100) / 100;
// Summe in Euro (Rechnungen in anderer Währung zum Kurs der Rechnung); fremd: eine davon ist nicht in Euro
const sumOf = (list: RtItem[], f: (i: RtItem) => number) => list.reduce((s, i) => s + inEurZahl(f(i), i.kurs), 0);
const fremdIn = (list: RtItem[]) => list.some((i) => i.waehrung !== 'EUR');
const UMGERECHNET = ' · umgerechnet zum Kurs der Rechnungen';
const ART_NAME = { normal: '', abschlag: 'Abschlagsrechnung', schluss: 'Schlussrechnung' } as const;
const docName = (i: RtItem) => (i.kind === 'storno' ? 'Stornorechnung' : i.kind === 'korrektur' ? 'Rechnungskorrektur' : ART_NAME[i.art] || 'Rechnung');
// Beleg für „Per E-Mail“ (VersandDialog.tsx) und die PDF dazu
type MahnRef = { id: string; level: 1 | 2 | 3 };
const rBeleg = (i: RtItem): VersandBeleg => ({ art: 'rechnung', id: i.id, titel: `${docName(i)} ${i.number}` });
const mBeleg = (m: MahnRef, i: RtItem): VersandBeleg => ({ art: 'mahnung', id: m.id, titel: `${MAHN_TITLES[m.level - 1]} zu ${i.number}` });
// „Per E-Mail“ im Fenster der PDF nicht bei stornierten Rechnungen und nicht bei Mahnungen zu erledigten Rechnungen
// (bei Rechnungskorrekturen schon)
const rView = (i: RtItem): View => ({
  url: pdfUrl(i.id),
  title: `${docName(i)} ${i.number}`,
  beleg: i.status === 'storniert' && i.kind !== 'korrektur' ? undefined : rBeleg(i),
});
const mView = (m: MahnRef, i: RtItem): View => ({ url: mahnUrl(m.id), title: `${MAHN_TITLES[m.level - 1]} ${i.number}`, beleg: isOpen(i) ? mBeleg(m, i) : undefined });

type Filter = 'alle' | 'offen' | 'ueberfaellig' | 'teilweise' | 'bezahlt' | 'storniert';
const FILTERS: [Filter, string][] = [
  ['alle', 'Alle'],
  ['offen', 'Offen'],
  ['ueberfaellig', 'Überfällig'],
  ['teilweise', 'Teilweise bezahlt'],
  ['bezahlt', 'Bezahlt'],
  ['storniert', 'Storniert'],
];
// Filter aus der Adresse, z. B. von den Kacheln der Übersicht: /rechnung/rechnungen?filter=ueberfaellig
const filterOf = (v: string | null): Filter => FILTERS.find(([f]) => f === v)?.[0] ?? 'alle';
function matches(i: RtItem, f: Filter, today: string) {
  if (f === 'offen') return isOpen(i);
  if (f === 'ueberfaellig') return isOverdue(i, today);
  if (f === 'teilweise') return i.kind === 'rechnung' && i.status === 'teilweise';
  if (f === 'bezahlt') return i.kind === 'rechnung' && i.status === 'bezahlt';
  // Rechnungskorrekturen nur unter „Alle“
  if (f === 'storniert') return i.kind === 'storno' || (i.kind === 'rechnung' && i.status === 'storniert');
  return true;
}
// Suche: Kunde, Rechnungsnummer (auch nur „17“, bei Storno und Korrektur auch die der Rechnung), Auftrag oder Betrag
// („1.190“, „1190,00“, „1.190,00 USD“)
function found(i: RtItem, q: string) {
  const t = q.trim().toLowerCase();
  if (!t) return true;
  const digits = t.replace(/[.\s€]|\b[a-z]{3}\b/g, '');
  return (
    i.customer.toLowerCase().includes(t) ||
    i.number.toLowerCase().includes(t) ||
    (i.stornoOfNumber ?? '').toLowerCase().includes(t) ||
    (i.auftrag ?? '').toLowerCase().includes(t) ||
    (digits !== '' && betragText(i.amount).replace(/[.\s]/g, '').includes(digits))
  );
}

// Alle geschriebenen Rechnungen einer Firma, neueste zuerst: oben die Lage auf einen Blick (offen, überfällig,
// bezahlt diesen Monat), darunter Filter und Suche
export function RechnungList({ data, reload }: { data: Overview; reload(): Promise<void> }) {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setView] = useState<View | null>(null);
  const [mahn, setMahn] = useState<RtItem | null>(null);
  const [zahl, setZahl] = useState<string | null>(null);
  const [storno, setStorno] = useState<RtItem | null>(null);
  // „Neu schreiben“ (Storno und Kopie) und „Gutschrift / Korrektur“ (Rechnungskorrektur)
  const [neuSchreiben, setNeuSchreiben] = useState<RtItem | null>(null);
  const [korrektur, setKorrektur] = useState<RtItem | null>(null);
  // Billtoro: Stundenzettel einer Rechnung (Menü „···“)
  const [zettel, setZettel] = useState<RtItem | null>(null);
  const [send, setSend] = useState<VersandBeleg | null>(null);
  // „Per Post versenden“ (nur wenn auf dem Server eingerichtet)
  const [brief, setBrief] = useState<PostBeleg | null>(null);
  const postAn = data.post?.an ?? false;
  // Billtoro: gleich nach dem Schreiben „Rechnung verschicken“ statt der PDF
  const [verschicken, setVerschicken] = useState<VerschickenBeleg | null>(null);
  // Billtoro: Funktionen, die der Tarif nicht enthält (null: vorhanden, in der Kanzlei-App immer)
  const tarif = data.tarif;
  const postFehlt = fehlendeFunktion(tarif, 'post');
  const mahnFehlt = fehlendeFunktion(tarif, 'mahnungen');
  const kiFehlt = data.ki ? fehlendeFunktion(tarif, 'ki') : null;
  const aboFehlt = fehlendeFunktion(tarif, 'abos') ?? grenzeFehlt(tarif, 'abos');
  // Mahnläufe: Stand je Zeile und „Nicht automatisch mahnen“ nur, wenn eingeschaltet (oder schon ein Mahnstopp da ist)
  const ml = data.mahnlauf;
  const mahnlaufFehlt = fehlendeFunktion(tarif, 'mahnlauf');
  // Billtoro: Rechte des Logins (nur Anzeige, der Server prüft); in der Kanzlei-App alles
  const rechnen = darf(tarif, 'rechnungen');
  const zahlen = darf(tarif, 'zahlungen');
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const urlFilter = params.get('filter');
  const [filter, setFilter] = useState<Filter>(() => filterOf(urlFilter));
  useEffect(() => setFilter(filterOf(urlFilter)), [urlFilter]);
  // Suche aus der Adresse, z. B. ?suche=RE-2026-0031 (Links im Vorgang bei den Aufträgen)
  const urlSuche = params.get('suche');
  const [q, setQ] = useState(urlSuche ?? '');
  useEffect(() => {
    if (urlSuche !== null) setQ(urlSuche);
  }, [urlSuche]);
  const [exp, setExp] = useState(false);
  // Billtoro: Recht „Einstellungen“ ohne „Exporte“: nur die DATEV-Angaben (sonst über „Exportieren“ → „DATEV“)
  const [datev, setDatev] = useState(false);
  const nurDatevAngaben = !darf(tarif, 'export') && darf(tarif, 'einstellungen') && !fehlendeFunktion(tarif, 'datev');
  const today = data.today;
  const open = data.items.filter(isOpen);
  const overdue = open.filter((i) => isOverdue(i, today));
  const month = today.slice(0, 7);
  const paidMonth = data.items.filter((i) => i.kind === 'rechnung' && i.status === 'bezahlt' && i.paidOn?.startsWith(month));
  const shown = data.items.filter((i) => matches(i, filter, today) && found(i, q));
  // das Fenster zeigt immer den neuesten Stand der Rechnung (nach Zahlung oder Rücknahme neu geladen)
  const zahlItem = zahl ? (data.items.find((i) => i.id === zahl) ?? null) : null;
  // Abschlagsrechnung → Schlussrechnung, die sie abzieht (nicht storniert)
  const abgezogenIn = new Map<string, string>();
  for (const i of data.items) {
    if (i.kind === 'rechnung' && i.art === 'schluss' && i.status !== 'storniert') for (const n of i.abzuege) abgezogenIn.set(n, i.number);
  }
  // je Zeile: graue Zeile, Knopf für den nächsten Schritt und Menü „···“ (RechnungZeile.ts)
  const ctx: ZeilenKontext = {
    today,
    rechnen,
    zahlen,
    angebote: darf(tarif, 'angebote'),
    postAn,
    mahnFehlt,
    mahnlaufFehlt: mahnlaufFehlt !== null,
    mahnlauf: ml?.an ? ml.modus : null,
    abgezogenIn,
    postText,
  };

  // Gerade geschrieben: gleich die PDF zeigen, zum Herunterladen und Verschicken. Billtoro: gleich „Rechnung
  // verschicken“ (per E-Mail, Teilen, per Post)
  useEffect(() => {
    const st = location.state as { show?: string; number?: string; title?: string } | null;
    if (st?.show) {
      const title = `${st.title ?? 'Rechnung'} ${st.number ?? ''}`;
      if (BILLTORO) setVerschicken({ id: st.show, titel: title, art: st.title ?? 'Rechnung' });
      else setView({ url: pdfUrl(st.show), title, beleg: { art: 'rechnung', id: st.show, titel: title } });
      navigate('.', { replace: true, state: null });
    }
  }, [location.state, navigate]);
  const verschickenItem = verschicken ? (data.items.find((i) => i.id === verschicken.id) ?? null) : null;
  const verschickenBeleg: VersandBeleg | null = verschicken ? { art: 'rechnung', id: verschicken.id, titel: verschicken.titel } : null;

  async function doStorno() {
    if (!storno) return;
    setBusy(true);
    try {
      const res = await api.post<{ number: string }>(`/api/rechnungstool/rechnung/${storno.id}/storno`);
      await reload();
      toast(`Stornorechnung ${res.number} geschrieben`);
      setStorno(null);
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  // Neu schreiben: stornieren, dann das Formular mit einer Kopie (Hinweis „Ersetzt Rechnung …“)
  async function doNeuSchreiben() {
    if (!neuSchreiben) return;
    const id = neuSchreiben.id;
    setBusy(true);
    try {
      const res = await api.post<{ number: string }>(`/api/rechnungstool/rechnung/${id}/storno`);
      await reload();
      toast(`Stornorechnung ${res.number} geschrieben`);
      setNeuSchreiben(null);
      navigate(`/rechnung/neu?vorlage=${id}&neuschreiben=1`);
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  // Mahnstopp an der Rechnung: der Mahnlauf lässt sie aus, von Hand mahnen geht weiter
  async function mahnstopp(i: RtItem) {
    const an = !i.mahnstopp;
    setBusy(true);
    try {
      await api.put(mahnstoppUrl(i.id), { an });
      await reload();
      toast(an ? 'Wird nicht mehr automatisch gemahnt' : 'Wird wieder automatisch gemahnt');
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  // Rechnungskorrektur geschrieben: wie nach einer neuen Rechnung gleich die PDF (Billtoro: „verschicken“)
  async function korrekturFertig(res: KorrekturErgebnis) {
    const w = korrektur?.waehrung ?? 'EUR';
    setKorrektur(null);
    await reload();
    toast(
      `Rechnungskorrektur ${res.number} geschrieben${
        res.erstattung > 0 ? `. Zu erstatten: ${eurC(res.erstattung, w)}. Tragen Sie die Erstattung ein, sobald Sie überwiesen haben.` : ''
      }`,
    );
    const titel = `Rechnungskorrektur ${res.number}`;
    if (BILLTORO) setVerschicken({ id: res.id, titel, art: 'Rechnungskorrektur' });
    else setView({ url: pdfUrl(res.id), title: titel, beleg: { art: 'rechnung', id: res.id, titel } });
  }

  // PDF geteilt (Teilen-Menü des Geräts) oder heruntergeladen: im Protokoll vermerken, die Zeile zählt es als
  // verschickt. Fehler still, das Teilen selbst hat geklappt.
  async function geteiltMelden(id: string, weg: 'teilen' | 'download') {
    if (!rechnen) return;
    try {
      await api.post(`/api/rechnungstool/rechnung/${id}/geteilt`, { weg });
      await reload();
    } catch {
      // nichts zu melden
    }
  }

  // Knopf für den nächsten Schritt der Zeile
  function knopf(s: NonNullable<Schritt>, i: RtItem) {
    if (s.art === 'erstattung') {
      return (
        <button type="button" className="btn small re-warn" onClick={() => setZahl(i.id)}>
          Erstattung eintragen
        </button>
      );
    }
    if (s.art === 'mahnen') {
      return s.gesperrt ? (
        // Billtoro: die Zahlungserinnerung ist in jedem Tarif, Mahnungen danach erst ab Start
        <GesperrtKnopf tarif={tarif} fehlt={s.gesperrt} className="btn small re-warn" onUpgrade={() => upgradeAnbieten(tarif, 'mahnungen')}>
          {mahnKnopf(s.stufe)}
        </GesperrtKnopf>
      ) : (
        <button type="button" className="btn small re-warn" onClick={() => setMahn(i)}>
          {mahnKnopf(s.stufe)}
        </button>
      );
    }
    if (s.art === 'mail') {
      return (
        <button type="button" className="btn small" onClick={() => setSend(rBeleg(i))}>
          Per E-Mail
        </button>
      );
    }
    return (
      <button type="button" className="btn small" onClick={() => setZahl(i.id)}>
        Zahlung eintragen
      </button>
    );
  }

  // Eintrag im Menü „···“ der Zeile
  function eintrag(e: Eintrag, i: RtItem) {
    const key = 'id' in e ? `${e.art}-${e.id}` : e.art;
    const m = 'id' in e ? i.mahnungen.find((x) => x.id === e.id) : undefined;
    const item = (text: ReactNode, onClick: () => void, className?: string) => (
      <button key={key} type="button" role="menuitem" className={className} onClick={onClick}>
        {text}
      </button>
    );
    switch (e.art) {
      case 'mail':
        return item('Per E-Mail', () => setSend(rBeleg(i)));
      case 'post':
        // Billtoro ohne Brief im Tarif: mit Marke (öffnet „Mehr mit Billtoro“) oder „bald“
        return postFehlt ? (
          <GesperrtKnopf key={key} role="menuitem" tarif={tarif} fehlt={postFehlt} className="" onUpgrade={() => upgradeAnbieten(tarif, 'post')}>
            Per Post versenden
          </GesperrtKnopf>
        ) : (
          item(t('Per Post versenden'), () => setBrief(rBeleg(i)))
        );
      case 'xml': {
        // E-Rechnung als XML laden (bei XRechnung die Datei für das Portal der Behörde)
        const x = i.eFormat === 'xrechnung';
        return (
          <a
            key={key}
            role="menuitem"
            href={`/api/rechnungstool/rechnung/${i.id}/xml`}
            download
            aria-label={`${x ? 'XRechnung' : 'E-Rechnung'} ${i.number} als XML laden`}
          >
            {x ? 'XRechnung (XML)' : 'E-Rechnung (XML)'}
          </a>
        );
      }
      case 'stundenzettel':
        return item(`Stundenzettel (${i.stundenzettel ?? 0})`, () => setZettel(i));
      case 'zahlung':
        return item('Zahlung eintragen', () => setZahl(i.id));
      case 'erstattung':
        return item('Erstattung eintragen', () => setZahl(i.id), 'rt-warn');
      case 'zahlungen':
        return item('Zahlungen', () => setZahl(i.id));
      case 'mahnen': {
        // nur mit Mahnlauf hier (sonst der Knopf der Zeile)
        const stufe = i.mahnungen.length as 0 | 1 | 2;
        return stufe > 0 && mahnFehlt ? (
          <GesperrtKnopf key={key} role="menuitem" tarif={tarif} fehlt={mahnFehlt} className="rt-warn" onUpgrade={() => upgradeAnbieten(tarif, 'mahnungen')}>
            {mahnKnopf(stufe)}
          </GesperrtKnopf>
        ) : (
          item(mahnKnopf(stufe), () => setMahn(i), 'rt-warn')
        );
      }
      case 'mahnung':
        return (
          m &&
          item(
            <>
              {MAHN_TITLES[m.level - 1]} {fmtDate(m.date)}
              {m.versendetAm && (
                <>
                  {' '}
                  <small className="muted re-menue-zusatz">per E-Mail am {fmtDate(m.versendetAm)}</small>
                </>
              )}
            </>,
            () => setView(mView(m, i)),
          )
        );
      case 'mahnungMail':
        return m && item(`${MAHN_TITLES[m.level - 1]} per E-Mail`, () => setSend(mBeleg(m, i)));
      case 'mahnstopp':
        return (
          <button key={key} type="button" role="menuitem" disabled={busy} onClick={() => void mahnstopp(i)}>
            {i.mahnstopp ? 'Wieder automatisch mahnen' : 'Nicht automatisch mahnen'}
          </button>
        );
      case 'korrektur':
        return item('Gutschrift / Korrektur', () => setKorrektur(i));
      case 'lieferschein':
        // Lieferschein zur Rechnung (nicht zu Abschlägen)
        return (
          <Link key={key} role="menuitem" to={neuerLieferschein({ art: 'rechnung', id: i.id })}>
            Lieferschein
          </Link>
        );
      case 'kopieren':
        return (
          <Link key={key} role="menuitem" to={`/rechnung/neu?vorlage=${i.id}`}>
            Kopieren
          </Link>
        );
      case 'abo':
        return aboFehlt ? (
          <GesperrtKnopf
            key={key}
            role="menuitem"
            tarif={tarif}
            fehlt={aboFehlt}
            className=""
            onUpgrade={() => upgradeAnbieten(tarif, 'abos') || grenzeAnbieten(tarif, 'abos')}
          >
            Wiederkehrend machen
          </GesperrtKnopf>
        ) : (
          <Link key={key} role="menuitem" to={`/rechnung/wiederkehrend/neu?rechnung=${i.id}`} aria-label={`${i.number} wiederkehrend machen`}>
            Wiederkehrend machen
          </Link>
        );
      case 'trenner':
        return <div key={key} role="separator" />;
      case 'neu':
        return item('Neu schreiben', () => setNeuSchreiben(i));
      case 'storno':
        return item('Stornieren', () => setStorno(i), 'rt-danger');
    }
  }

  return (
    <>
      <div className="rt-head">
        <div>
          <h1>
            {data.firm.name}
            <NurAnsehen tarif={tarif} bereich="rechnungen" />
          </h1>
          <p className="muted">
            {data.items.length ? `${data.items.length} ${data.items.length === 1 ? 'Rechnung' : 'Rechnungen'}` : 'Noch keine Rechnungen'}
            <NutzungHinweis tarif={tarif} wo="liste" />
          </p>
        </div>
        <div className={`rt-head-act${data.ki ? ' sz-head-act' : ''}`}>
          {data.items.length > 0 && darf(tarif, 'export') && (
            <button type="button" className="btn" onClick={() => setExp(true)}>
              Exportieren
            </button>
          )}
          {nurDatevAngaben && (
            <button type="button" className="btn" onClick={() => setDatev(true)}>
              DATEV-Angaben
            </button>
          )}
          {data.ki &&
            rechnen &&
            (kiFehlt ? (
              <GesperrtKnopf tarif={tarif} fehlt={kiFehlt} className="btn" onUpgrade={() => upgradeAnbieten(tarif, 'ki')}>
                Stundenzettel hochladen
              </GesperrtKnopf>
            ) : (
              <Link to="/rechnung/stundenzettel" className="btn">
                Stundenzettel hochladen
              </Link>
            ))}
          {rechnen && (
            <Link to="/rechnung/neu" className="btn primary">
              + Neue Rechnung
            </Link>
          )}
        </div>
      </div>

      <Entwuerfe
        firmId={data.firm.id}
        art="rechnung"
        today={data.today}
        weiter={(id) => `/rechnung/neu?entwurf=${id}`}
        aendern={rechnen}
        aktion={
          data.ki &&
          rechnen &&
          !kiFehlt && (
            <Link to="/rechnung/stundenzettel" className="linkbtn">
              Stundenzettel hochladen
            </Link>
          )
        }
      />

      <MahnlaufHinweis mahnlauf={ml} tarif={tarif} wo="liste" />

      {data.items.length > 0 && (
        <div className="rt-kpis">
          <button type="button" className={`rt-kpi${filter === 'offen' ? ' on' : ''}`} onClick={() => setFilter(filter === 'offen' ? 'alle' : 'offen')}>
            <span className="rt-kpi-l">Offen</span>
            <span className="rt-kpi-v num">{eurC(sumOf(open, (i) => i.offen))}</span>
            <span className="rt-kpi-s">
              {open.length === 1 ? '1 Rechnung' : `${open.length} Rechnungen`}
              {fremdIn(open) ? UMGERECHNET : ''}
            </span>
          </button>
          <button
            type="button"
            className={`rt-kpi${overdue.length ? ' bad' : ''}${filter === 'ueberfaellig' ? ' on' : ''}`}
            onClick={() => setFilter(filter === 'ueberfaellig' ? 'alle' : 'ueberfaellig')}
          >
            <span className="rt-kpi-l">Überfällig</span>
            <span className="rt-kpi-v num">{eurC(sumOf(overdue, (i) => i.offen))}</span>
            <span className="rt-kpi-s">
              {overdue.length ? `${overdue.length === 1 ? '1 Rechnung' : `${overdue.length} Rechnungen`} · jetzt mahnen` : 'nichts überfällig'}
              {fremdIn(overdue) ? UMGERECHNET : ''}
            </span>
          </button>
          <button type="button" className={`rt-kpi ok${filter === 'bezahlt' ? ' on' : ''}`} onClick={() => setFilter(filter === 'bezahlt' ? 'alle' : 'bezahlt')}>
            <span className="rt-kpi-l">Bezahlt</span>
            <span className="rt-kpi-v num">{eurC(sumOf(paidMonth, geld))}</span>
            <span className="rt-kpi-s">
              im {MONTHS[Number(month.slice(5)) - 1]} · {paidMonth.length === 1 ? '1 Rechnung' : `${paidMonth.length} Rechnungen`}
              {fremdIn(paidMonth) ? UMGERECHNET : ''}
            </span>
          </button>
        </div>
      )}

      {data.items.length > 0 && (
        <div className="rt-tools">
          <input
            type="search"
            className="rt-search"
            value={q}
            placeholder="Suchen: Kunde, Nummer, Auftrag oder Betrag"
            aria-label="Rechnungen suchen"
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="rt-chips" role="group" aria-label="Filter">
            {FILTERS.map(([f, label]) => (
              <button key={f} type="button" className={`rt-chip${filter === f ? ' on' : ''}`} aria-pressed={filter === f} onClick={() => setFilter(f)}>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {data.items.length === 0 ? (
        <div className="rt-card rt-empty">
          <p>Schreiben Sie Ihre erste Rechnung. Nummer, Datum und Umsatzsteuer rechnet die Seite für Sie.</p>
          {rechnen && (
            <Link to="/rechnung/neu" className="btn primary">
              Erste Rechnung schreiben
            </Link>
          )}
        </div>
      ) : shown.length === 0 ? (
        <div className="rt-card rt-empty">
          <p>Keine Rechnung passt zu dieser Auswahl.</p>
          <button
            type="button"
            className="btn small"
            onClick={() => {
              setFilter('alle');
              setQ('');
            }}
          >
            Alle zeigen
          </button>
        </div>
      ) : (
        <ul className="rt-list re-list">
          {shown.map((i) => {
            const schritt = naechsterSchritt(i, ctx);
            const eintraege = menueEintraege(i, ctx, schritt);
            const info = infoZeile(i, ctx);
            // Storno und Korrektur: Bezug oben, der Kunde in der grauen Zeile
            const titel =
              i.kind === 'storno'
                ? `Storno zu ${i.stornoOfNumber}`
                : i.kind === 'korrektur'
                  ? `Korrektur zu ${i.stornoOfNumber}`
                  : `${ART_NAME[i.art] ? `${ART_NAME[i.art]} · ` : ''}${i.customer}`;
            return (
              <li key={i.id} className="rt-row">
                <button type="button" className="rt-row-main" onClick={() => setView(rView(i))}>
                  <span className="rt-no num">{i.number}</span>
                  <span className="re-kunde">
                    <span className="rt-cust" title={titel}>
                      {i.aboId && (
                        <span className="rt-abo-z" role="img" aria-label="Aus einer wiederkehrenden Rechnung" title="Aus einer wiederkehrenden Rechnung">
                          ↻
                        </span>
                      )}
                      {i.mahnstopp && isOpen(i) && (
                        <span className="badge b-none rt-mahnstopp" title="Nicht automatisch mahnen">
                          Mahnstopp
                        </span>
                      )}
                      {titel}
                    </span>
                    {/* bdi: eigene Schreibrichtung, sonst springt im Arabischen (Kanzlei-App) der Punkt nach dem Datum nach vorn */}
                    {info && (
                      <span className={`rt-row-info${info.bad ? ' bad' : ''}`}>
                        <bdi>{info.teile.join(' · ')}</bdi>
                      </span>
                    )}
                  </span>
                  <span className="rt-date muted">{fmtDate(i.invoiceDate)}</span>
                  <span className={`rt-amt num${i.amount < 0 ? ' neg' : ''}`}>{eurC(i.amount, i.waehrung)}</span>
                  <Status item={i} today={today} />
                </button>
                {(schritt || eintraege.length > 0) && (
                  <div className="re-act">
                    {schritt && <div className="rt-next">{knopf(schritt, i)}</div>}
                    {eintraege.length > 0 && <ZeilenMenue nummer={i.number}>{eintraege.map((e) => eintrag(e, i))}</ZeilenMenue>}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <ExportDialog data={data} open={exp} onClose={() => setExp(false)} />
      <DatevDialog data={data} open={datev} onClose={() => setDatev(false)} />
      <PdfDialog
        url={view?.url ?? null}
        title={view?.title ?? ''}
        download={view ? `${view.url}?download=1` : undefined}
        onClose={() => setView(null)}
        onMail={
          view?.beleg && rechnen
            ? () => {
                setSend(view.beleg ?? null);
                setView(null);
              }
            : undefined
        }
        onPost={
          postAn && rechnen && !postFehlt && view?.beleg?.art === 'rechnung'
            ? () => {
                setBrief(view.beleg ?? null);
                setView(null);
              }
            : undefined
        }
        // Rechnung, Storno oder Korrektur heruntergeladen (keine Mahnung): zählt in der Zeile als verschickt
        onDownload={rechnen && view?.beleg?.art === 'rechnung' ? () => view.beleg && void geteiltMelden(view.beleg.id, 'download') : undefined}
      />
      <VersandDialog beleg={send} tarif={tarif} onClose={() => setSend(null)} onSent={reload} />
      <PostDialog beleg={brief} tarif={tarif} onClose={() => setBrief(null)} onSent={reload} />
      {BILLTORO && (
        <VerschickenDialog
          beleg={verschicken}
          item={verschickenItem}
          onClose={() => setVerschicken(null)}
          onMail={() => {
            setSend(verschickenBeleg);
            setVerschicken(null);
          }}
          onPost={
            postAn && tarif?.features.post
              ? () => {
                  setBrief(verschickenBeleg);
                  setVerschicken(null);
                }
              : undefined
          }
          onAnsehen={() => {
            if (verschickenBeleg) setView({ url: pdfUrl(verschickenBeleg.id), title: verschickenBeleg.titel, beleg: verschickenBeleg });
            setVerschicken(null);
          }}
          onGeteilt={(id, weg) => void geteiltMelden(id, weg)}
        />
      )}
      <MahnDialog
        item={mahn}
        onClose={() => setMahn(null)}
        onDone={async (m, item, mail) => {
          setMahn(null);
          await reload();
          toast(`${MAHN_TITLES[m.level - 1]} geschrieben`);
          // gleich per E-Mail (Fenster „Per E-Mail“), sonst die PDF zeigen
          if (mail) setSend(mBeleg(m, item));
          else setView(mView(m, item));
        }}
      />
      <ZahlDialog item={zahlItem} today={today} kann={zahlen} reload={reload} onClose={() => setZahl(null)} />
      <ConfirmDialog
        open={!!storno}
        title={`${storno ? docName(storno) : 'Rechnung'} ${storno?.number ?? ''} stornieren?`}
        text={`${
          storno && geld(storno) > 0
            ? `Es entsteht eine Stornorechnung mit eigener Nummer und negativem Betrag. Ihr Kunde hat schon ${eurC(geld(storno), storno.waehrung)} bezahlt: Das zahlen Sie zurück oder verrechnen es und tragen danach die Erstattung ein.`
            : 'Es entsteht eine Stornorechnung mit eigener Nummer und negativem Betrag. Schicken Sie sie Ihrem Kunden. Danach können Sie eine neue, richtige Rechnung schreiben.'
        }${
          // Billtoro: aus Zeiten geschrieben
          storno?.zeiten
            ? storno.zeiten.anzahl === 1
              ? ' Die Zeit daraus ist danach wieder offen.'
              : ` Die ${storno.zeiten.anzahl} Zeiten daraus sind danach wieder offen.`
            : ''
        }`}
        confirmLabel="Ja, stornieren"
        danger
        busy={busy}
        onConfirm={() => void doStorno()}
        onCancel={() => setStorno(null)}
      />
      <ConfirmDialog
        open={!!neuSchreiben}
        title={`${neuSchreiben ? docName(neuSchreiben) : 'Rechnung'} ${neuSchreiben?.number ?? ''} neu schreiben?`}
        text={`Die Rechnung wird storniert (Stornorechnung mit eigener Nummer), dann öffnet sich eine Kopie zum Ändern. Schicken Sie danach beides Ihrem Kunden.${
          neuSchreiben && geld(neuSchreiben) > 0
            ? ` Ihr Kunde hat schon ${eurC(geld(neuSchreiben), neuSchreiben.waehrung)} bezahlt: Verrechnen Sie das mit der neuen Rechnung oder zahlen Sie es zurück, und tragen Sie bei der alten die Erstattung ein.`
            : ''
        }`}
        confirmLabel="Ja, neu schreiben"
        busy={busy}
        onConfirm={() => void doNeuSchreiben()}
        onCancel={() => setNeuSchreiben(null)}
      />
      <KorrekturDialog item={korrektur} firmId={data.firm.id} onClose={() => setKorrektur(null)} onDone={korrekturFertig} />
      <StundenzettelFenster
        open={!!zettel}
        titel={zettel ? `Stundenzettel zu ${zettel.number}` : ''}
        rechnungId={zettel?.id}
        onClose={() => setZettel(null)}
      />
    </>
  );
}

function Status({ item, today }: { item: RtItem; today: string }) {
  if (item.kind === 'storno') return <span className="badge b-none">Stornorechnung</span>;
  if (item.kind === 'korrektur') return <span className="badge b-none">Rechnungskorrektur</span>;
  if (item.erstattung > 0) return <span className="badge b-warn">Erstattung offen</span>;
  if (item.status === 'storniert') return <span className="badge b-none">Storniert</span>;
  // ganz ausgebucht, ohne Geld (z. B. Kunde zahlt nicht): erledigt, aber nicht bezahlt
  if (item.status === 'bezahlt' && item.ausgebucht > 0 && geld(item) <= 0) return <span className="badge b-none">Ausgebucht</span>;
  if (item.status === 'bezahlt') return <span className="badge b-ok">Bezahlt</span>;
  const last = item.mahnungen[item.mahnungen.length - 1];
  if (last) return <span className="badge b-bad">{MAHN_TITLES[last.level - 1]}</span>;
  if (daysUntil(item.dueDate, today) < 0) return <span className="badge b-bad">Überfällig</span>;
  return <span className="badge b-warn">{item.status === 'teilweise' ? 'Teilweise bezahlt' : 'Offen'}</span>;
}

const ZAHL_NAME: Record<ZahlArt, string> = {
  zahlung: 'Zahlung',
  bauabzug: 'Bauabzug (15 % ans Finanzamt)',
  ausbuchung: 'Ausgebucht',
  erstattung: 'Erstattung',
};
const ZAHL_CHIP: Record<ZahlArt, string> = {
  zahlung: 'Zahlung erhalten',
  bauabzug: 'Bauabzug',
  ausbuchung: 'Rest ausbuchen',
  erstattung: 'Erstattung',
};
const QUELLE: Record<ZahlEintrag['quelle'], string> = { hand: '', abgleich: 'aus dem Kontoauszug', uebernahme: 'von früher übernommen' };

// Zahlungen einer Rechnung: Liste aller Einträge (nichts wird geändert, falsche Einträge werden zurückgenommen) und
// neuer Eintrag. Teilzahlungen, Bauabzug, Ausbuchen (mit Grund) und Erstattung nach Überzahlung oder Storno. Alle
// Beträge in der Währung der Rechnung; Bauabzug nur in Euro.
// kann: Zahlungen eintragen und zurücknehmen (Billtoro: Recht „Zahlungen“); ohne nur die Einträge zum Ansehen. Gilt
// wie beim Öffnen (MitRecht): Nimmt der Inhaber das Recht weg, bleibt der neue Eintrag mit den Eingaben stehen, der
// Server antwortet beim Eintragen mit 403 und Text.
function ZahlDialog({
  item,
  today,
  kann,
  reload,
  onClose,
}: {
  item: RtItem | null;
  today: string;
  kann: boolean;
  reload(): Promise<void>;
  onClose(): void;
}) {
  const toast = useToast();
  const w = item?.waehrung ?? 'EUR';
  const storniert = item?.status === 'storniert';
  const bauSchon = item
    ? item.zahlungen.filter((z) => z.art === 'bauabzug').reduce((s, z) => s + (z.ruecknahme ? -z.betrag : z.betrag), 0)
    : 0;
  const bauFrei = item ? Math.max(0, Math.round((item.bauabzug - bauSchon) * 100) / 100) : 0;
  // Vorschläge neu setzen, wenn das Fenster für eine Rechnung aufgeht oder sich ihr Stand ändert
  const key = item ? `${item.id}-${item.zahlungen.length}` : null;
  const [shownFor, setShownFor] = useState<string | null>(null);
  const [kannOffen, setKannOffen] = useState(kann);
  const darfHier = key === shownFor ? kannOffen : kann;
  const arten: ZahlArt[] = !item || !darfHier
    ? []
    : storniert
      ? item.erstattung > 0
        ? ['erstattung']
        : []
      : [
          ...(item.offen > 0 ? (['zahlung'] as const) : []),
          ...(item.offen > 0 && bauFrei > 0 && w === 'EUR' ? (['bauabzug'] as const) : []),
          ...(item.offen > 0 ? (['ausbuchung'] as const) : []),
          ...(item.erstattung > 0 ? (['erstattung'] as const) : []),
        ];
  const vorschlag = (a: ZahlArt) =>
    !item ? 0 : a === 'erstattung' ? item.erstattung : a === 'bauabzug' ? Math.min(bauFrei, item.offen) : item.offen;
  const [art, setArt] = useState<ZahlArt>('zahlung');
  const [betrag, setBetrag] = useState('');
  const [datum, setDatum] = useState(today);
  const [notiz, setNotiz] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zurueck, setZurueck] = useState<ZahlEintrag | null>(null);
  // Rest als Skonto ausbuchen (null: von selbst, wenn fristgerecht gezahlt)
  const [skWahl, setSkWahl] = useState<boolean | null>(null);
  if (key !== shownFor) {
    setShownFor(key);
    setKannOffen(kann);
    const a = arten[0] ?? 'zahlung';
    setArt(a);
    setBetrag(item ? betragText(vorschlag(a)) : '');
    setDatum(today);
    setNotiz('');
    setError(null);
    setZurueck(null);
    setSkWahl(null);
  }

  function pick(a: ZahlArt) {
    setArt(a);
    setBetrag(betragText(vorschlag(a)));
    setError(null);
    setSkWahl(null);
  }

  // Skonto der Rechnung (wie auf der PDF, vom Rechnungsbetrag nach Rechnungskorrekturen): Hat der Kunde Rest minus
  // Skonto gezahlt, den Rest mit einem Klick als Skonto ausbuchen; vorgeschlagen, wenn er bis zur Skontofrist gezahlt hat
  const sk = item && item.kind === 'rechnung' && !storniert ? skontoBetrag(item.skonto, item.invoiceDate, Math.round(gemindert(item) * 100)) : null;
  const offenC = item ? Math.round(item.offen * 100) : 0;
  const skZahlen = sk && offenC > sk.cents ? offenC - sk.cents : null;
  const b = parseEuro(betrag);
  const bC = typeof b === 'number' ? Math.round(b * 100) : null;
  const skRest = sk && art === 'zahlung' && bC != null && bC > 0 && bC < offenC && offenC - bC <= sk.cents ? offenC - bC : 0;
  const skPuenktlich = !!sk && datum <= sk.bis;
  const skAn = skRest > 0 && (skWahl ?? skPuenktlich);

  async function submit() {
    if (!item) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/rechnungstool/rechnung/${item.id}/zahlung`, {
        art,
        betrag: typeof b === 'number' ? b : NaN,
        datum,
        notiz: notiz.trim() || null,
        ...(skAn ? { skonto: skRest / 100 } : {}),
      });
      await reload();
      toast(`${ZAHL_NAME[art]} zu ${item.number} eingetragen${skAn ? `, ${eurC(skRest / 100, w)} Skonto ausgebucht` : ''}`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function doZurueck() {
    if (!item || !zurueck) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/rechnungstool/zahlung/${zurueck.id}/zurueck`, {});
      await reload();
      toast('Eintrag zurückgenommen');
    } catch (err) {
      setError(errorText(err));
      setZurueck(null);
    } finally {
      setBusy(false);
    }
  }

  const extra = item ? Math.round((item.forderung - gemindert(item)) * 100) / 100 : 0;
  return (
    <Dialog open={!!item} onClose={onClose} title={item ? `Zahlungen zu ${item.number}` : ''} className="rt-zahl-dlg">
      {item && (
        <>
          <p className="muted" style={{ marginTop: 0 }}>
            {item.customer}
            {item.kind === 'rechnung' && !storniert ? ` · fällig am ${fmtDate(item.dueDate)}` : ' · storniert'}
          </p>
          <div className="rt-sums rt-sums-dlg">
            <div>
              <span>Rechnungsbetrag</span>
              <span className="num">{eurC(item.amount, w)}</span>
            </div>
            {item.korrigiert > 0 && (
              <div>
                <span>Gutgeschrieben</span>
                <span className="num">− {eurC(item.korrigiert, w)}</span>
              </div>
            )}
            {extra > 0 && (
              <div>
                <span>Mahngebühr und Zinsen</span>
                <span className="num">{eurC(extra, w)}</span>
              </div>
            )}
            {geld(item) !== 0 && (
              <div>
                <span>Bezahlt</span>
                <span className="num">− {eurC(geld(item), w)}</span>
              </div>
            )}
            {item.ausgebucht > 0 && (
              <div>
                <span>Ausgebucht</span>
                <span className="num">− {eurC(item.ausgebucht, w)}</span>
              </div>
            )}
            {item.erstattung > 0 ? (
              <div className="rt-total">
                <span>An den Kunden zu erstatten</span>
                <span className="num">{eurC(item.erstattung, w)}</span>
              </div>
            ) : (
              !storniert && (
                <div className="rt-total">
                  <span>Noch offen</span>
                  <span className="num">{eurC(item.offen, w)}</span>
                </div>
              )
            )}
          </div>

          {item.zahlungen.length > 0 && (
            <ul className="rt-zahl-list" aria-label="Bisherige Einträge">
              {item.zahlungen.map((z) => (
                <li key={z.id} className={z.zurueckgenommen ? 'off' : z.ruecknahme ? 'back' : ''}>
                  <span className="rt-zahl-d">{fmtDate(z.datum)}</span>
                  <span className="rt-zahl-t">
                    {z.ruecknahme ? `Rücknahme: ${ZAHL_NAME[z.art]}` : ZAHL_NAME[z.art]}
                    {(z.notiz || QUELLE[z.quelle]) && <small>{[z.notiz, QUELLE[z.quelle]].filter(Boolean).join(' · ')}</small>}
                  </span>
                  <span className="rt-zahl-b num">
                    {(z.art === 'erstattung') !== z.ruecknahme ? '+ ' : '− '}
                    {eurC(z.betrag, w)}
                  </span>
                  {darfHier && !z.ruecknahme && !z.zurueckgenommen ? (
                    <button type="button" className="linkbtn rt-danger" onClick={() => setZurueck(z)}>
                      Zurücknehmen
                    </button>
                  ) : (
                    <span className="rt-zahl-x muted">{z.zurueckgenommen ? 'zurückgenommen' : ''}</span>
                  )}
                </li>
              ))}
            </ul>
          )}

          {zurueck ? (
            <div className="notice warn rt-zahl-confirm">
              <p>
                {ZAHL_NAME[zurueck.art]} über {eurC(zurueck.betrag, w)} vom {fmtDate(zurueck.datum)} zurücknehmen? Der Eintrag bleibt sichtbar,
                dazu kommt ein Gegeneintrag von heute.
              </p>
              <div className="dlg-actions">
                <button type="button" className="btn" onClick={() => setZurueck(null)}>
                  Abbrechen
                </button>
                <button type="button" className="btn dark" disabled={busy} onClick={() => void doZurueck()}>
                  Ja, zurücknehmen
                </button>
              </div>
            </div>
          ) : arten.length > 0 ? (
            <>
              <h3 className="rt-zahl-h">Neuer Eintrag</h3>
              {arten.length > 1 && (
                <ChipRadios
                  label="Art"
                  className="rt-zahl-arten"
                  value={art}
                  options={arten.map((a) => [a, ZAHL_CHIP[a]] as const)}
                  onChange={pick}
                />
              )}
              <p className="rt-hint">
                {art === 'zahlung' && 'Auch Teilzahlungen. Was übrig bleibt, bleibt offen.'}
                {art === 'zahlung' && sk && skZahlen != null && (
                  <span className="rt-skonto">
                    {' '}
                    Mit Skonto bis zum {fmtDate(sk.bis)}: {eurC(skZahlen / 100, w)}.{' '}
                    <button type="button" className="linkbtn" onClick={() => setBetrag(betragText(skZahlen / 100))}>
                      Mit Skonto gezahlt
                    </button>
                  </span>
                )}
                {art === 'bauabzug' &&
                  `Ihr Kunde hat 15 % einbehalten und ans Finanzamt gezahlt, höchstens ${eurC(item.bauabzug, w)}. Bitte nur mit seiner Abrechnung eintragen.${
                    item.kundeArt === 'privat' ? ' Privatkunden behalten ihn nur ein, wenn sie mehr als zwei Wohnungen vermieten.' : ''
                  }`}
                {art === 'ausbuchung' &&
                  'Der Rest wird nicht mehr gefordert, z. B. Skonto, erlassene Mahngebühr oder Kunde zahlt nicht. Bitte den Grund eintragen. Bei Skonto oder Ausfall berichtigt Ihre Buchhaltung die Umsatzsteuer.'}
                {art === 'erstattung' && 'Geld an den Kunden zurückgezahlt oder mit einer anderen Rechnung verrechnet.'}
              </p>
              <div className="rt-grid rt-grid2">
                <label className="field">
                  <span>{w === 'EUR' ? 'Betrag' : `Betrag in ${w}`}</span>
                  <input value={betrag} inputMode="decimal" onChange={(e) => setBetrag(e.target.value)} />
                </label>
                {w !== 'EUR' && (
                  <p className="muted rt-span2 rt-hint">
                    Bitte den Betrag in {w} eintragen, wie er auf der Rechnung steht. Kursunterschiede bucht Ihr Steuerbüro.
                  </p>
                )}
                <label className="field">
                  <span>Datum</span>
                  <input type="date" value={datum} max={today} min={item.zahlbarAb} onChange={(e) => setDatum(e.target.value)} />
                </label>
                {skRest > 0 && sk && (
                  <label className="check rt-span2 rt-skonto-aus">
                    <input type="checkbox" checked={skAn} onChange={(e) => setSkWahl(e.target.checked)} />
                    <span>
                      Rest {eurC(skRest / 100, w)} als Skonto ausbuchen
                      <br />
                      <small className="muted">
                        {skPuenktlich
                          ? `Gezahlt bis zum ${fmtDate(sk.bis)}: Der Kunde darf Skonto abziehen. Danach ist die Rechnung bezahlt.`
                          : `Die Skontofrist endete am ${fmtDate(sk.bis)}. Nur ankreuzen, wenn Sie das Skonto trotzdem gewähren.`}
                      </small>
                    </span>
                  </label>
                )}
                <label className="field rt-span2">
                  <span>{art === 'ausbuchung' ? 'Grund' : 'Notiz (optional)'}</span>
                  <input
                    value={notiz}
                    maxLength={300}
                    placeholder={art === 'ausbuchung' ? 'z. B. 2 % Skonto' : ''}
                    onChange={(e) => setNotiz(e.target.value)}
                  />
                </label>
              </div>
            </>
          ) : null}
          {error && <div className="notice bad">{error}</div>}
          <div className="dlg-actions">
            <button type="button" className="btn" onClick={onClose}>
              Schließen
            </button>
            {!zurueck && arten.length > 0 && (
              <button type="button" className="btn primary" disabled={busy} onClick={() => void submit()}>
                Eintragen
              </button>
            )}
          </div>
        </>
      )}
    </Dialog>
  );
}

// Mahnung schreiben (3.10): Die Zahlungserinnerung ist kostenlos. Danach bei Privatkunden nur echte Kosten (Porto,
// Material, höchstens 5 €), bei Firmen und Behörden einmal die Verzugspauschale von 40 € (§ 288 Abs. 5 BGB). Zinsen auf
// Wunsch ab Verzugsbeginn (5 bzw. 9 Prozentpunkte über dem Basiszins, § 288 Abs. 1 und 2 BGB), die rechnet der Server
// bis heute aus. Im Fenster wie bei Lexware nur „Mahngebühr“ (bei Firmen die Pauschale von 40 €) und „Zinsen berechnen“
// (Sam 13.10.); die Mahnung selbst nennt die Pauschale und die Zinsen wie bisher.
const ZINS_HINWEIS = 'Rechnet die App bis heute.';
const ZINS_WEITER = 'Die Zinsen der letzten Mahnung laufen bis heute weiter.';
const KUNDEN_ART: [KundenArt, string][] = [
  ['firma', 'Firma'],
  ['privat', 'Privatperson'],
  ['behoerde', 'Behörde'],
];
function MahnDialog({
  item,
  onClose,
  onDone,
}: {
  item: RtItem | null;
  onClose(): void;
  onDone(m: MahnRef, item: RtItem, mail: boolean): Promise<void>;
}) {
  const level = item ? item.mahnungen.length : 0;
  // Beträge in der Währung der Rechnung; Gebühr und Pauschale in Euro festgelegt, zum Kurs der Rechnung umgerechnet
  const w = item?.waehrung ?? 'EUR';
  const inW = (euro: number) => (w === 'EUR' ? euro : ausEur(Math.round(euro * 100), item!.kurs) / 100);
  const [kundeArt, setKundeArt] = useState<KundenArt | ''>('');
  const [fee, setFee] = useState('0');
  const [pauschale, setPauschale] = useState(true);
  const [zinsen, setZinsen] = useState(false);
  const [days, setDays] = useState('7');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shownFor, setShownFor] = useState<string | null>(null);
  // danach gleich das Fenster „Per E-Mail“ (sonst die PDF); die Wahl gilt weiter, solange die Seite offen ist
  const [mail, setMail] = useState(true);
  // ältere Rechnung ohne Kundenart: die Angabe aus der ersten Mahnung gilt weiter (der Server merkt sie sich)
  const kArt = item?.kundeArt ?? (kundeArt || null);
  // Zinsen der letzten Mahnung: laufen weiter (neu gerechnet bis heute), erlassen geht über „Rest ausbuchen“
  const lastZins = item?.mahnungen[item.mahnungen.length - 1]?.zins ?? 0;
  // Vorschläge neu setzen, wenn das Fenster für eine andere Rechnung oder Stufe aufgeht
  const key = item ? `${item.id}-${level}` : null;
  if (key !== shownFor) {
    setShownFor(key);
    setKundeArt('');
    setFee('0');
    setPauschale(true);
    setZinsen(item?.kundeArt === 'firma' || item?.kundeArt === 'behoerde' || (item?.mahnungen.some((m) => m.zins > 0) ?? false));
    setDays('7');
    setError(null);
  }
  function pickArt(a: KundenArt) {
    setKundeArt(a);
    setZinsen(a !== 'privat');
  }
  const earlier = item ? item.mahnungen.reduce((s, m) => s + m.fee, 0) : 0;
  const hadPauschale = item ? item.mahnungen.some((m) => m.pauschale) : false;
  const f = parseEuro(fee);
  const newFee =
    level === 0 || !kArt
      ? 0
      : kArt === 'privat'
        ? typeof f === 'number'
          ? inW(f)
          : 0
        : pauschale && !hadPauschale
          ? Math.max(0, Math.round((inW(40) - earlier) * 100) / 100)
          : 0;
  const withZins = level > 0 && !!kArt && (zinsen || lastZins > 0);
  const fees = item ? item.mahnungen.reduce((s, m) => s + m.fee, 0) : 0;

  async function submit() {
    if (!item) return;
    setBusy(true);
    setError(null);
    try {
      const m = await api.post<{ id: string; level: 1 | 2 | 3 }>(`/api/rechnungstool/rechnung/${item.id}/mahnung`, {
        fee: kArt === 'privat' && level > 0 ? (f === null ? 0 : (f ?? NaN)) : 0,
        pauschale,
        zinsen: withZins,
        ...(item.kundeArt ? {} : kArt ? { kundeArt: kArt } : {}),
        days: days.trim() === '' ? NaN : Number(days),
      });
      await onDone(m, item, mail);
    } catch (err) {
      // Tarif-Fehler (Billtoro): das Fenster „Mehr mit Billtoro“ übernimmt, dieses geht zu
      if (tarifFehler(err)) onClose();
      else setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={!!item} onClose={onClose} title={item ? `${MAHN_TITLES[level]} zu ${item.number}` : ''}>
      {item && (
        <>
          <p className="muted" style={{ marginTop: 0 }}>
            An {item.customer}. Fällig war die Rechnung am {fmtDate(item.dueDate)}.
          </p>
          {!item.kundeArt && (
            <div className="field">
              <span>Ihr Kunde ist</span>
              <ChipRadios label="Kundenart" value={kundeArt} options={KUNDEN_ART} onChange={pickArt} />
              <small>Davon hängt die Mahngebühr ab.</small>
            </div>
          )}
          {level === 0 ? (
            <p className="rt-hint">Die Zahlungserinnerung ist kostenlos. Mahngebühr und Zinsen gibt es ab der nächsten Mahnung.</p>
          ) : kArt === 'privat' ? (
            <>
              <label className="field">
                <span>{w === 'EUR' ? 'Mahngebühr' : 'Mahngebühr in Euro'}</span>
                <input value={fee} inputMode="decimal" onChange={(e) => setFee(e.target.value)} />
                <small>
                  Nur echte Kosten wie Porto, höchstens 5,00 €.{w !== 'EUR' && ` Wird zum Kurs der Rechnung in ${w} umgerechnet.`}
                </small>
              </label>
              <label className="check">
                <input type="checkbox" checked={withZins} disabled={lastZins > 0} onChange={(e) => setZinsen(e.target.checked)} />
                <span>
                  Zinsen berechnen
                  <br />
                  <small className="muted">{lastZins > 0 ? ZINS_WEITER : ZINS_HINWEIS}</small>
                </span>
              </label>
            </>
          ) : kArt ? (
            <>
              <label className="check">
                <input type="checkbox" checked={pauschale && !hadPauschale} disabled={hadPauschale} onChange={(e) => setPauschale(e.target.checked)} />
                <span>
                  {w === 'EUR' ? 'Mahngebühr 40 €' : `Mahngebühr 40 € (≈ ${eurC(inW(40), w)})`}
                  <br />
                  <small className="muted">
                    {hadPauschale
                      ? 'Schon berechnet, nur einmal je Rechnung.'
                      : earlier > 0
                        ? `Pauschal einmal je Rechnung, die bisherigen ${eurC(earlier, w)} werden angerechnet.`
                        : 'Pauschal einmal je Rechnung.'}
                  </small>
                </span>
              </label>
              <label className="check">
                <input type="checkbox" checked={withZins} disabled={lastZins > 0} onChange={(e) => setZinsen(e.target.checked)} />
                <span>
                  Zinsen berechnen
                  <br />
                  <small className="muted">{lastZins > 0 ? ZINS_WEITER : ZINS_HINWEIS}</small>
                </span>
              </label>
            </>
          ) : null}
          <label className="field">
            <span>Neue Frist in Tagen</span>
            <input value={days} inputMode="numeric" onChange={(e) => setDays(e.target.value)} />
          </label>
          <div className="rt-sums rt-sums-dlg">
            <div>
              <span>Rechnungsbetrag</span>
              <span className="num">{eurC(item.amount, w)}</span>
            </div>
            {item.korrigiert > 0 && (
              <div>
                <span>Gutgeschrieben</span>
                <span className="num">− {eurC(item.korrigiert, w)}</span>
              </div>
            )}
            {item.bezahlt !== 0 && (
              <div>
                <span>Schon beglichen</span>
                <span className="num">− {eurC(item.bezahlt, w)}</span>
              </div>
            )}
            {fees > 0 && (
              <div>
                <span>Bisherige Mahngebühren</span>
                <span className="num">{eurC(fees, w)}</span>
              </div>
            )}
            {newFee > 0 && (
              <div>
                <span>Mahngebühr</span>
                <span className="num">{eurC(newFee, w)}</span>
              </div>
            )}
            <div className="rt-total">
              <span>Offener Betrag</span>
              {/* die Zinsen der letzten Mahnung rechnet die neue bis heute neu */}
              <span className="num">{eurC(Math.max(0, Math.round((item.offen - lastZins + newFee) * 100) / 100), w)}</span>
            </div>
            {withZins && <small className="muted">zuzüglich Zinsen bis heute, sie stehen auf der Mahnung</small>}
          </div>
          <label className="check">
            <input type="checkbox" checked={mail} onChange={(e) => setMail(e.target.checked)} />
            <span>Danach per E-Mail an den Kunden schicken</span>
          </label>
          {error && <div className="notice bad">{error}</div>}
          <div className="dlg-actions">
            <button type="button" className="btn" onClick={onClose}>
              Abbrechen
            </button>
            <button type="button" className="btn primary" disabled={busy || (!item.kundeArt && !kundeArt)} onClick={() => void submit()}>
              {MAHN_TITLES[level]} schreiben
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
}
